import React, { useState, useEffect } from 'react';
import { certificatesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Certificate, Download, Calendar } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export default function WorkerCertificatesPage() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await certificatesApi.getAll({ worker_id: user?.id });
      setCertificates(res.data);
    } catch (error) {
      toast.error('Error al cargar certificados');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (cert) => {
    try {
      const response = await certificatesApi.download(cert.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado_${cert.code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificado descargado');
    } catch (error) {
      toast.error('Error al descargar certificado');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="worker-certificates-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Mis Certificados</h1>
        <p className="text-[#2B4B8C]/60">Certificados obtenidos en tus capacitaciones</p>
      </div>

      {/* Certificates Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {certificates.length > 0 ? certificates.map((cert) => (
          <Card 
            key={cert.id} 
            className="bg-white border-[#2B4B8C]/10 hover:border-[#2BBAA4]/30 transition-colors"
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-lg bg-[#2BBAA4]/10 flex items-center justify-center flex-shrink-0">
                  <Certificate size={28} className="text-[#2BBAA4]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-[#2B4B8C] font-semibold truncate">{cert.training_name}</h3>
                      <Badge className="mt-1 bg-[#2B4B8C]/10 text-[#2B4B8C] border-[#2B4B8C]/20 border text-xs">
                        {cert.training_type}
                      </Badge>
                    </div>
                    <Badge className={cert.is_valid ? 'bg-green-500/10 text-green-400 border-green-500/20 border' : 'bg-red-500/10 text-red-400 border-red-500/20 border'}>
                      {cert.is_valid ? 'Válido' : 'Inválido'}
                    </Badge>
                  </div>
                  
                  <div className="mt-4 p-3 rounded-lg bg-[#f8fafc]">
                    <p className="text-xs text-[#2B4B8C]/60 mb-1">Código de Verificación</p>
                    <p className="text-sm font-mono text-[#2BBAA4]">{cert.code}</p>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div className="space-y-1">
                      <span className="text-sm text-[#2B4B8C]/60 flex items-center gap-1">
                        <Calendar size={14} />
                        {format(new Date(cert.issued_date), "dd MMM yyyy", { locale: es })}
                      </span>
                      {cert.expiry_date && (
                        <span className={`text-xs flex items-center gap-1 ${new Date(cert.expiry_date) < new Date() ? 'text-red-500' : 'text-[#2B4B8C]/50'}`}>
                          Vigencia: {format(new Date(cert.expiry_date), "dd MMM yyyy", { locale: es })}
                          {new Date(cert.expiry_date) < new Date() && ' (Vencido)'}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleDownload(cert)}
                      className="bg-[#2BBAA4] text-black hover:bg-[#239986]"
                    >
                      <Download size={16} className="mr-1" />
                      Descargar
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="bg-white border-[#2B4B8C]/10 md:col-span-2">
            <CardContent className="py-12 text-center text-[#2B4B8C]/50">
              <Certificate size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aún no tienes certificados</p>
              <p className="text-sm mt-1">Completa tus capacitaciones para obtenerlos</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
